import React, { useMemo, useState } from 'react';
import {
  Crown, Check, X, Info, ArrowRight, Percent, Coins, Droplet, CalendarClock, Receipt,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useGetPlanQuery,
  useDowngradePlanMutation,
  useUpgradeToPremiumMutation,
  useGetPlanPaymentsQuery,
  useGetUpgradeOptionsQuery,
} from '../store/api/planApi';
import { useTheme } from '../context/ThemeContext';
import PremiumCheckout from '../components/billing/PremiumCheckout';
import StatusBadge from '../components/common/StatusBadge';
import CryptoLogo from '../components/common/CryptoLogo';
import Pager from '../components/common/Pager';
import { formatUSD } from '../utils/formatAmount';
import './Billing.css';

// There is deliberately no fallback price in this bundle. The price comes from
// PREMIUM_PRICE_USD in the backend environment and can change without a
// frontend deploy, so a baked-in figure would be shown confidently and be wrong
// -- briefly printing "$49.00" before snapping to the real amount is a worse
// answer than printing nothing for a moment.

// What Premium buys. The numbers in this list mirror the backend's
// DEFAULT_PLAN_FEES; the figures actually SHOWN on the page come from the
// merchant's effective plan, so a Custom plan never reads a catalog row.
// Fees are payout fees -- see the hero stats below.
const PREMIUM_FEATURES = [
  '1% payout fee (half of Free)',
  'Lower minimum payout fees',
  'Priority payouts',
  'Advanced analytics & reporting',
  'Scheduled report emails',
  'Priority support',
];

// Features lost when downgrading Premium → Free.
const FEATURES_LOST = [
  'Reduced 1% payout fee (reverts to 2%)',
  'Lower minimum payout fees',
  'Priority payouts',
  'Advanced analytics & reporting',
  'Scheduled report emails',
  'Priority support',
];

// Billing history window. Receipts are kept indefinitely, so the default is a
// window rather than everything -- an account a few years old would otherwise
// open on a wall of rows.
const HISTORY_RANGES = [
  { key: '3m', label: 'Last 3 months', months: 3 },
  { key: '6m', label: 'Last 6 months', months: 6 },
  { key: '12m', label: 'Last 12 months', months: 12 },
  { key: 'all', label: 'All time', months: null },
];

const HISTORY_PAGE_SIZE = 5;

const formatDate = (value) => {
  if (!value) return null;
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const Billing = () => {
  const { data: plan, isLoading, refetch: refetchPlan } = useGetPlanQuery();
  const { data: payments = [], refetch: refetchPayments } = useGetPlanPaymentsQuery();
  const [downgradePlan, { isLoading: downgrading }] = useDowngradePlanMutation();
  const [upgradeToPremium, { isLoading: startingUpgrade }] = useUpgradeToPremiumMutation();
  const { data: options, refetch: refetchOptions } = useGetUpgradeOptionsQuery();
  // Premium is paid in real mainnet USDT. In Test mode the merchant is working
  // with testnet funds, so the upgrade is shown but cannot be started.
  const { mode } = useTheme();
  const isTestMode = mode === 'test';

  const [checkout, setCheckout] = useState(null);
  const [network, setNetwork] = useState('ethereum');
  const [showDowngrade, setShowDowngrade] = useState(false);
  const [historyRange, setHistoryRange] = useState('6m');
  const [historyPage, setHistoryPage] = useState(1);

  // Filter then page, both client-side: /merchant/plan/payments returns the
  // whole history in one response, and a merchant's receipt count is measured
  // in dozens, not thousands.
  const visiblePayments = useMemo(() => {
    const months = HISTORY_RANGES.find((r) => r.key === historyRange)?.months;
    if (!months) return payments;
    const floor = new Date();
    floor.setMonth(floor.getMonth() - months);
    return payments.filter((p) => new Date(p.confirmedAt || p.createdAt) >= floor);
  }, [payments, historyRange]);

  const pageCount = Math.max(1, Math.ceil(visiblePayments.length / HISTORY_PAGE_SIZE));
  // Narrowing the window can leave the current page past the end.
  const page = Math.min(historyPage, pageCount);
  const pageRows = visiblePayments.slice((page - 1) * HISTORY_PAGE_SIZE, page * HISTORY_PAGE_SIZE);

  if (isLoading) {
    return (
      <div className="billing-page">
        <div className="billing-loading">
          <div className="billing-spinner" />
          <p>Loading your plan...</p>
        </div>
      </div>
    );
  }

  const planKey = plan?.planKey || 'free';
  const isPremium = planKey === 'premium';
  const isCustom = planKey === 'custom';
  const feePct = plan?.feePct;
  const minGeneral = plan?.minimums?.general;
  const minEth = plan?.minimums?.eth;
  const renewal = formatDate(plan?.grandfatheredUntil);
  // When Premium ends unless renewed. Premium is paid in USDT and never charged
  // automatically, so this is an end date, not a billing date.
  const premiumEnds = formatDate(plan?.currentPeriodEnd);

  const handleStartUpgrade = async () => {
    if (isTestMode) return;
    try {
      // The server returns an existing open charge rather than minting a second
      // one, so pressing this twice -- or reloading mid-payment -- resumes
      // instead of stranding whatever has already been sent.
      const charge = await upgradeToPremium({ network }).unwrap();
      setCheckout(charge);
      // The new charge is a row in the history (pending until paid).
      refetchPayments();
    } catch (err) {
      toast.error(err?.data?.message || 'Could not start upgrade');
    }
  };

  const handleUpgradeConfirmed = () => {
    toast.success('Payment confirmed - you are now on Premium');
    refetchPlan();
    // Any overpayment has just become credit; the next charge needs to see it.
    refetchOptions();
    // The confirmed charge and its receipt number, without a manual reload.
    refetchPayments();
  };

  const handleConfirmDowngrade = async () => {
    try {
      await downgradePlan().unwrap();
      toast.success('Premium will be cancelled at the end of your billing period');
      setShowDowngrade(false);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to cancel Premium');
    }
  };

  return (
    <div className="billing-page">
      <div className="rd-page-header">
        <div>
          <h1 className="rd-page-title">Billing</h1>
          <p className="rd-page-subtitle">Manage your plan, payment method, and invoice history.</p>
        </div>
      </div>

      {/* Hero, in two bands rather than one row.
          Identity and the action across the top, the figures on an even grid
          below. Flowing all six blocks through a single flex row left the stats
          fighting the plan name and the button for width, and they wrapped at
          whatever point was left over rather than lining up.
          Everything is read off the effective plan, so a Custom plan shows its
          real negotiated figures rather than a catalog row. */}
      <div className="bill-hero">
        <div className="bill-hero__top">
          <div className="bill-hero__plan">
            <span className="bill-hero__crest" aria-hidden="true"><Crown size={22} /></span>
            <div>
              <span className="bill-hero__eyebrow">Current Plan</span>
              <div className="bill-hero__name">
                <h2>{planKey}</h2>
                <span className="bill-hero__status">{plan?.status || 'active'}</span>
              </div>
              <p className="bill-hero__blurb">
                {isPremium
                  ? 'Access to all features, higher limits and priority support.'
                  : 'Upgrade any time to halve your payout fee and lower your minimum payout fees.'}
              </p>
            </div>
          </div>

          {/* {!isCustom && (
            <button
              type="button"
              className="bill-hero__cta"
              onClick={isPremium ? () => setShowDowngrade(true) : handleStartUpgrade}
              disabled={!isPremium && (isTestMode || startingUpgrade || !!checkout)}
              title={!isPremium && isTestMode ? 'Switch to Live mode to upgrade' : undefined}
            >
              {isPremium ? 'Manage Plan' : startingUpgrade ? 'Starting...' : 'Upgrade to Premium'}
              <ArrowRight size={16} />
            </button>
          )} */}  
        </div>

        <div className="bill-hero__stats">
          {[
            // These are PAYOUT fees: taken when the merchant withdraws
            // (feeCalculator.js, payout.controller). Incoming payments and
            // auto-conversions carry no MaxelPay fee, so "per transaction"
            // described a charge that never happens.
            { icon: Percent, label: 'Payout Fee',
              value: feePct != null ? `${feePct}%` : '\u2014', sub: 'of each payout amount' },
            { icon: Coins, label: 'Minimum Payout Fee',
              value: minGeneral != null ? `$${formatUSD(minGeneral)}` : '\u2014', sub: 'per payout (stablecoins & other tokens)' },
            { icon: Droplet, label: 'Minimum Payout Fee (ETH)',
              value: minEth != null ? `$${formatUSD(minEth)}` : '\u2014', sub: 'per payout in ETH' },
            { icon: CalendarClock,
              label: isPremium ? 'Active Until' : 'Grandfathered Until',
              value: (isPremium ? premiumEnds : renewal) || '\u2014',
              sub: isPremium
                ? (plan?.cancelAtPeriodEnd ? 'then moves to Free' : 'renew before this date')
                : renewal ? 'current pricing held' : 'no end date' },
          ].map((stat) => (
            <div className="bill-hero__stat" key={stat.label}>
              <span className="bill-hero__stat-icon" aria-hidden="true"><stat.icon size={15} /></span>
              <span className="bill-hero__stat-label">{stat.label}</span>
              <strong>{stat.value}</strong>
              <span className="bill-hero__stat-sub">{stat.sub}</span>
            </div>
          ))}
        </div>
      </div>

      {plan?.grandfatheredUntil && !isPremium && (
        <div className="grandfathered-note">
          <Info size={16} />
          <span>
            Your current pricing is grandfathered until {renewal}. Upgrade any time to lock in
            lower Premium fees.
          </span>
        </div>
      )}

      {/* History beside the thing that adds to it: the checkout when one is
          open, the upgrade offer when it is not. */}
      <div className="bill-grid">
        <div className="rd-card bill-history">
          <div className="bill-history__head">
            <div>
              <h2>Billing history</h2>
              <p>View your past payments and subscription details.</p>
            </div>
            <label className="bill-range">
              <CalendarClock size={15} aria-hidden="true" />
              <select
                value={historyRange}
                onChange={(e) => { setHistoryRange(e.target.value); setHistoryPage(1); }}
                aria-label="Billing history period"
              >
                {HISTORY_RANGES.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </label>
          </div>

          {visiblePayments.length === 0 ? (
            <div className="bill-history__empty">
              <span className="bill-history__empty-icon"><Receipt size={22} /></span>
              <h3>No payments in this period</h3>
              <p>
                {payments.length > 0
                  ? 'Try a longer period — your earlier receipts are still here.'
                  : 'Your Premium receipts will appear here once you upgrade.'}
              </p>
            </div>
          ) : (
            <>
              <div className="bill-history__scroll">
                <table className="bill-table">
                  <thead>
                    <tr>
                      <th>Receipt #</th>
                      <th>Date</th>
                      <th>Plan</th>
                      <th className="bill-num">Amount</th>
                      <th>Token</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((p) => (
                      <tr key={p.id}>
                        {/* A receipt number is minted on confirmation, so a
                            charge that expired or is still open has none. Three
                            rows of bare dashes read as missing data rather than
                            as "there was nothing to issue". */}
                        <td data-label="Receipt #" className="rd-mono">
                          {p.receiptNumber || <span className="bill-no-receipt">Not issued</span>}
                        </td>
                        <td data-label="Date">{formatDate(p.confirmedAt || p.createdAt) || '—'}</td>
                        <td data-label="Plan" className="bill-plan">{p.planKey}</td>
                        <td data-label="Amount" className="rd-mono bill-num">
                          ${formatUSD(p.amountUsd)}
                          {/* A part-paid row has to explain itself, or it reads
                              as "we took your money and gave you nothing". */}
                          {p.status === 'partially_paid' && (
                            <span className="billing-part-paid">
                              ${formatUSD(p.amountPaidUsd)} received
                            </span>
                          )}
                        </td>
                        <td data-label="Token">
                          <span className="bill-token">
                            <CryptoLogo symbol={p.token} size={20} />
                            {p.token}
                          </span>
                        </td>
                        <td data-label="Status"><StatusBadge status={p.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {visiblePayments.length > HISTORY_PAGE_SIZE && (
                <Pager
                  page={page}
                  pageCount={pageCount}
                  total={visiblePayments.length}
                  pageSize={HISTORY_PAGE_SIZE}
                  onPage={setHistoryPage}
                />
              )}
            </>
          )}
        </div>

        <div className="bill-side">
          {checkout && !isTestMode ? (
            /* Connected wallet only. Hidden in Test mode, where nothing on this
               page may start or continue a real payment. */
            <PremiumCheckout
              charge={checkout}
              onClose={() => { setCheckout(null); refetchOptions(); refetchPayments(); refetchPlan(); }}
              onConfirmed={handleUpgradeConfirmed}
            />
          ) : isCustom ? (
            <div className="rd-card rd-card-pad bill-offer">
              <h3>Custom plan</h3>
              <p className="bill-offer__blurb">
                Your pricing is negotiated and managed by our team. Talk to your account
                contact to change it.
              </p>
            </div>
          ) : (
            <div className="rd-card rd-card-pad bill-offer">
              <h3>{isPremium ? 'Your Premium plan' : 'Upgrade to Premium'}</h3>
              <div className="bill-offer__price rd-mono">
                {options?.priceUsd != null ? (
                  <>${formatUSD(options.priceUsd)}<small> /mo</small></>
                ) : (
                  <span className="bill-offer__price-wait" aria-label="Loading price">&mdash;</span>
                )}
              </div>

              <ul className="bill-offer__features">
                {PREMIUM_FEATURES.map((f) => (
                  <li key={f}><Check size={15} />{f}</li>
                ))}
              </ul>

              {isPremium && (
                <p className="plan-credit plan-active-until">
                  {premiumEnds ? `Active until ${premiumEnds}. ` : ''}
                  {plan?.cancelAtPeriodEnd
                    ? 'Cancelled: it will not renew. Renew below to keep Premium.'
                    : 'Renewing early adds a month after your current end date.'}
                </p>
              )}

              {/* The pay controls serve both a first upgrade and a renewal: a
                  payment while Premium is running extends it (see
                  confirmPremiumPayment). */}
              <>
                  {/* Chain is chosen before the charge is opened, because the
                      charge records it and the address is watched per chain.
                      The list comes from the server so it cannot drift. */}
                  <div className="plan-network" role="group" aria-label="Pay on">
                    <span className="plan-network__label">Pay in USDT on</span>
                    <div className="plan-network__opts">
                      {(options?.assets || [
                        { key: 'ethereum', label: 'Ethereum' },
                        { key: 'bsc', label: 'BNB Chain' },
                      ]).map((a) => (
                        <button
                          key={a.key}
                          type="button"
                          className={`plan-network__opt ${network === a.key ? 'is-active' : ''}`}
                          onClick={() => setNetwork(a.key)}
                          disabled={isTestMode}
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {options?.creditUsd > 0 && (
                    <p className="plan-credit">
                      ${formatUSD(options.creditUsd)} credit from a previous overpayment
                      will be applied first.
                    </p>
                  )}

                  {isTestMode && (
                    <p className="plan-test-note">
                      <Info size={14} aria-hidden="true" />
                      Premium is paid in real USDT. Switch to Live mode to {isPremium ? 'renew' : 'upgrade'}.
                    </p>
                  )}

                  <button
                    type="button"
                    className="rd-btn rd-btn-purple rd-btn-block"
                    onClick={handleStartUpgrade}
                    disabled={isTestMode || startingUpgrade}
                  >
                    {startingUpgrade ? 'Starting...' : isPremium ? 'Renew Premium' : 'Upgrade to Premium'}
                  </button>

                  {isPremium && !plan?.cancelAtPeriodEnd && (
                    <button
                      type="button"
                      className="rd-btn rd-btn-block btn-plan-danger plan-cancel-btn"
                      onClick={() => setShowDowngrade(true)}
                    >
                      Cancel Premium
                    </button>
                  )}
              </>
            </div>
          )}
        </div>
      </div>

      {/* Downgrade confirmation modal */}
      {showDowngrade && (
        <div className="billing-modal-overlay" onClick={() => !downgrading && setShowDowngrade(false)}>
          <div className="billing-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Cancel Premium?</h2>
            <p>You will lose the following Premium benefits:</p>
            <ul className="lost-features">
              {FEATURES_LOST.map((f) => (
                <li key={f}>
                  <X size={16} />
                  {f}
                </li>
              ))}
            </ul>
            <div className="billing-modal-warning">
              Your Premium plan stays active until the end of the current billing period
              {renewal ? ` (${renewal})` : ''}. The downgrade to Free takes effect then — no
              partial refunds.
            </div>
            <div className="billing-modal-actions">
              <button
                className="rd-btn rd-btn-secondary"
                onClick={() => setShowDowngrade(false)}
                disabled={downgrading}
              >
                Keep Premium
              </button>
              <button
                className="rd-btn btn-confirm-danger"
                onClick={handleConfirmDowngrade}
                disabled={downgrading}
              >
                {downgrading ? 'Cancelling...' : 'Cancel Premium'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
